/**
 * Chain Registry — the single source of truth for Descout's multi-chain support.
 *
 * Shared between the frontend (Next.js/TypeScript — via the typed wrapper
 * `src/lib/chains.ts`) and the backend (Express/Node — via `require()`). Written in
 * CJS for cross-compatibility, mirroring `monitored-collections.js`.
 *
 * Entry points:
 *   - Frontend:  import { CHAINS, getChain, ... } from '@/lib/chains';   // typed
 *   - Backend:   const { CHAINS, getChain } = require('../../../shared/config/chains.js');
 *
 * Field semantics (see docs/superpowers/specs/2026-06-05-chain-first-class-surfaces.md):
 *   - status:     PUBLIC display state. 'live' = fully-resourced hub; 'mvc' = thin,
 *                 just-stood-up (gets the "coverage just launched" thin-state UX);
 *                 'planned' = a known chain that is not yet a covered Descout ecosystem.
 *   - selectable: AUTHORING gate — may an operator pick this as an entity `chains[]`
 *                 value in the tag modal? DISTINCT from `status` (a chain can be
 *                 selectable for tagging before it has a public hub — e.g. Ethereum as
 *                 a cross-chain home, or chain #2 during MVC seeding).
 *   - feeds:      how to populate the chain — slugs consumed by the data services
 *                 (DefiLlama/DexScreener/CoinGecko/OpenSea) in B1.4/B1.6/B1.7. Abstract's
 *                 slugs match what the services hardcode today, so Abstract is unchanged.
 *   - ecosystem:  is this chain a covered-or-candidate Descout ECOSYSTEM — i.e. does it
 *                 belong in the ecosystems lens switcher? DISTINCT from both `status` and
 *                 `selectable`: Ethereum is `selectable` (cross-chain entities legitimately
 *                 live there) but is a cross-chain home rather than an ecosystem we cover,
 *                 and the NFT-collection chains are carried only for the collection form.
 *                 A chain shows in the lens dropdown from the moment it's a declared
 *                 candidate — grayed as "Soon" until its `status` leaves 'planned'.
 *   - recapDataReady: SAFETY gate (distinct from status/selectable). Are the daily-recap
 *                 DATA inputs (activity pulses, TVL, DEX volume, token prices) verified
 *                 chain-filtered for this chain? Until true, EcosystemRecapService
 *                 HARD-THROWS rather than risk publishing one chain's data under another
 *                 chain's headline (the armed footgun). Only Abstract today; flip a chain
 *                 to true only after its recap feeds are wired AND backfilled AND verified.
 *   - recapReviewRequired: PROCESS gate (distinct from recapDataReady). Must this chain's
 *                 generated recap be REVIEWED before it may publish? DEFAULT FALSE — today's
 *                 posture is auto-publish-at-generation (Lam 2026-09-04: "auto-publish stays on
 *                 until the reviewer lane is proven"). Flip to true and generation lands
 *                 draft+needsReview+reviewRequiredHold even with autoPublish on, and the only
 *                 route to published is review-recap-draft.js -> ops.js schedule-headless -> drip.
 *                 See docs/ops/HEADLESS-RECAP-REVIEW.md.
 *   - hubSlug:    route segment for the per-chain hub `/<hubSlug>` (may differ from id;
 *                 e.g. 'avalanche' -> '/avax'). Hubs land in B2/B3; the registry is ready.
 *   - tagline / thinStateMessage / seoLabel / explorerUrl: per-chain copy + links for the
 *                 Ecosystems rail, MVC thin-state card, SEO metadata, and asset deep-links.
 *
 * Convention: the FIRST chain in an entity's `chains[]` is its HOME chain — it drives the
 * OG/TVL stamp and the SEO canonical (a cross-chain entity still appears in every hub it
 * belongs to). See `getHomeChain()` in `src/lib/chains.ts`.
 *
 * To add a chain: add an entry below (+ `public/chain-<id>.png`), set status/selectable,
 * verify `feeds`. The add-a-chain runbook (B4) automates the rest.
 *
 * @typedef {'live' | 'mvc' | 'planned'} ChainStatus
 * @typedef {Object} ChainFeeds
 * @property {string} [tvl]    DefiLlama chain slug (e.g. 'Abstract')
 * @property {string} [tokens] DexScreener chain path segment (e.g. 'abstract')
 * @property {string} [nft]    OpenSea/NFT chain slug (e.g. 'abstract')
 * @property {string} [price]  CoinGecko asset-platform id (e.g. 'abstract')
 * @typedef {Object} ChainMeta
 * @property {string} id
 * @property {string} label
 * @property {string} color
 * @property {string} logo
 * @property {string} ogCard
 * @property {ChainStatus} status
 * @property {boolean} selectable
 * @property {boolean} [ecosystem]  Covered-or-candidate Descout ecosystem — shows in the
 *                 ecosystems lens switcher (grayed "Soon" while status === 'planned').
 * @property {boolean} [recapDataReady]
 * @property {boolean} [recapReviewRequired]  Must this chain's recap clear the headless
 *                 recap-review lane before it may publish? Default false.
 * @property {string} hubSlug
 * @property {ChainFeeds} feeds
 * @property {string} [tagline]
 * @property {string} [thinStateMessage]
 * @property {string} [seoLabel]
 * @property {string} [explorerUrl]
 * @property {string} [settlesOn]  Chain id this chain settles to (L2 → L1). The home
 *                 for the "Abstract settles on Ethereum" fact, which is a DIFFERENT
 *                 relation than app→protocol BUILT_ON — so it lives in config, NOT as
 *                 a chain entity / typed edge (2026-06-08 ontology decision §1).
 * @property {string} [operator]   Slug of the `organization` entity that operates this
 *                 chain (e.g. 'coinbase' for Base). Points AT an org node; the chain
 *                 itself is never a node. The home for "Base is operated by Coinbase."
 */

/** @type {Record<string, ChainMeta>} */
const CHAINS = {
  abstract: {
    id: 'abstract',
    label: 'Abstract',
    color: '#08D878', // Abstract brand green, sampled from public/Abstract_AppIcon_LightMode.webp
    logo: '/chain-abstract.png',
    ogCard: '/og-images/chain-abstract.png',
    status: 'live',
    selectable: true,
    ecosystem: true, // the one covered ecosystem today
    recapDataReady: true, // recap data inputs are Abstract-native today — verified safe
    hubSlug: 'abstract',
    tagline: 'The consumer crypto chain.',
    seoLabel: 'Abstract blockchain',
    explorerUrl: 'https://abscan.org',
    settlesOn: 'ethereum', // Abstract is an Ethereum L2 — config fact, not a chain-node edge
    operator: 'abstract-blockchain', // operated by Abstract (an org entity) — chain stays a non-node
    feeds: { tvl: 'Abstract', tokens: 'abstract', nft: 'abstract', price: 'abstract' },
    // K9/EO3 — per-chain LENS copy for the knowledge_pages overview synthesis
    // (backend/src/services/lens-page-ingest.js). `ecosystemLabel` names the cluster in the
    // page title + heading ("Abstract gaming ecosystem"); `coreClause` is the trailing
    // characterization appended to the lead sentence ("…, with gaming projects at its core.").
    // Abstract is gaming-centric; a chain WITHOUT a `lens` block gets the neutral default
    // (getChainLensConfig) so a second chain lens never inherits "gaming".
    lens: { ecosystemLabel: 'gaming ecosystem', coreClause: 'with gaming projects at its core' },
  },
  base: {
    id: 'base',
    label: 'Base',
    color: '#0052FF',
    logo: '/chain-base.png',
    ogCard: '/og-images/chain-base.png',
    status: 'planned',
    selectable: true, // already in KNOWN_CHAINS today; operators tag cross-/multi-chain entities
    ecosystem: true, // chain #2 — shows grayed "Soon" in the lens switcher until standup
    hubSlug: 'base',
    tagline: "Coinbase's onchain home.",
    seoLabel: 'Base (L2)',
    explorerUrl: 'https://basescan.org',
    settlesOn: 'ethereum', // Base is an Ethereum L2
    operator: 'coinbase', // operated by Coinbase (an org entity) — chain stays a non-node
    feeds: { tvl: 'Base', tokens: 'base', nft: 'base', price: 'base' },
  },
  ethereum: {
    id: 'ethereum',
    label: 'Ethereum',
    color: '#627EEA',
    logo: '/chain-ethereum.png',
    ogCard: '/og-images/chain-ethereum.png',
    status: 'planned', // a cross-chain home, not a covered Descout ecosystem (no hub)
    selectable: true, // cross-chain entities (e.g. Pudgy) legitimately live here
    hubSlug: 'ethereum',
    seoLabel: 'Ethereum',
    explorerUrl: 'https://etherscan.io',
    feeds: { tvl: 'Ethereum', tokens: 'ethereum', nft: 'ethereum', price: 'ethereum' },
  },
  avalanche: {
    id: 'avalanche',
    label: 'Avalanche',
    color: '#E84142',
    logo: '/chain-avalanche.png',
    ogCard: '/og-images/chain-avalanche.png',
    status: 'mvc', // AVAX round one live 2026-08-11 — entities + edges + embeddings landed, first coverage published
    selectable: true, // authoring gate OPEN 2026-08-06 (AVAX round one) — operators can tag chains:['avalanche']
    ecosystem: true, // stood up 2026-08-11 — no longer grayed "Soon" in the lens switcher
    // RECAP: ENABLED 2026-09-04 (Session 3 — "we still don't have any recaps for avax").
    // `recapDataReady: true` is a SAFETY assertion, not a feature flag: it says every input
    // the recap prompt consumes is chain-filtered and verified for this chain, so a recap
    // published under an Avalanche headline contains Avalanche facts only.
    //
    // WHAT CHANGED SINCE THE 2026-08-14 "not ready" verdict (both ❌ are closed):
    //   ✅ TOKEN FEED — fixed. Prod `tracked_tokens` is now 38 docs, every one carrying a
    //      `chain`; 9 priced avalanche rows (BTC.b, COQ, GMX, JOE, PNG, QI, sAVAX, WAVAX,
    //      XAVA), so getTokenPricesForChainId('avalanche') returns real movers.
    //   ✅ NFT FEED — no longer a blocker because the section is now explicitly OMITTED for
    //      every non-default chain (EcosystemRecapService.fetchNFTData returns [] early, with
    //      the reasoning inline): Avalanche's NFT venue is Joepegs, the lookup is
    //      OpenSea-slug-keyed, and prod `tracked_collections` holds zero avalanche docs. An
    //      absent section is honest; a borrowed one is not. Abstract's path is untouched.
    //   ✅ ACTIVITY PULSES — real but THIN after the chain-context firewall (9 raw → 2 kept
    //      over 08-31→09-02). Deliberately left thin: the fix is growing the AVAX handle
    //      registry, never loosening the firewall.
    //   ✅ TVL / DEX — live (TVL $492.98M, DEX 24h $71.35M on 09-03). The top "protocols by
    //      TVL" rows are CEX/bridge custody (Binance CEX, OKX, Bitfinex) and are filtered out
    //      of the prompt for non-default chains — see NON_ECOSYSTEM_PROTOCOL_CATEGORIES.
    //   ✅ BREAKING NEWS — the strongest input: 20 AVAX beats kept in 21 days, running to
    //      09-03 (Abstract's stopped 08-22).
    //
    // PREREQUISITE, shipped in the same change: recap doc ids are chain-keyed
    // (`recap_avalanche_<date>`; Abstract keeps `recap_<date>`). Flipping this flag WITHOUT
    // that id change would have made Avalanche silently never generate — `getRecap(date)`
    // would find Abstract's doc and return `{skipped:true}` — or, under forceRegenerate,
    // OVERWRITE Abstract's published recap. Never flip a chain here without checking
    // EcosystemRecapService.recapDocId covers it.
    recapDataReady: true,
    hubSlug: 'avax',
    tagline: 'High-throughput L1 + subnets.',
    seoLabel: 'Avalanche (AVAX)',
    explorerUrl: 'https://snowtrace.io',
    feeds: { tvl: 'Avalanche', tokens: 'avalanche', nft: 'avalanche', price: 'avalanche' },
  },

  // --- NFT-collection chains (carried for the collection form; not covered ecosystems) ---
  ronin: {
    id: 'ronin', label: 'Ronin', color: '#1273EA',
    logo: '/chain-ronin.png', ogCard: '/og-images/chain-ronin.png',
    status: 'planned', selectable: false, hubSlug: 'ronin',
    explorerUrl: 'https://app.roninchain.com', feeds: { nft: 'ronin' },
  },
  polygon: {
    id: 'polygon', label: 'Polygon', color: '#8247E5',
    logo: '/chain-polygon.png', ogCard: '/og-images/chain-polygon.png',
    status: 'planned', selectable: false, hubSlug: 'polygon',
    explorerUrl: 'https://polygonscan.com', feeds: { nft: 'polygon' },
  },
  arbitrum: {
    id: 'arbitrum', label: 'Arbitrum', color: '#28A0F0',
    logo: '/chain-arbitrum.png', ogCard: '/og-images/chain-arbitrum.png',
    status: 'planned', selectable: false, hubSlug: 'arbitrum',
    explorerUrl: 'https://arbiscan.io', feeds: { nft: 'arbitrum' },
  },
  optimism: {
    id: 'optimism', label: 'Optimism', color: '#FF0420',
    logo: '/chain-optimism.png', ogCard: '/og-images/chain-optimism.png',
    status: 'planned', selectable: false, hubSlug: 'optimism',
    explorerUrl: 'https://optimistic.etherscan.io', feeds: { nft: 'optimism' },
  },
  solana: {
    id: 'solana', label: 'Solana', color: '#14F195',
    logo: '/chain-solana.png', ogCard: '/og-images/chain-solana.png',
    status: 'planned', selectable: false, hubSlug: 'solana',
    explorerUrl: 'https://solscan.io', feeds: { nft: 'solana' },
  },
  bsc: {
    id: 'bsc', label: 'BNB Chain', color: '#F0B90B',
    logo: '/chain-bsc.png', ogCard: '/og-images/chain-bsc.png',
    status: 'planned', selectable: false, hubSlug: 'bsc',
    explorerUrl: 'https://bscscan.com', feeds: { nft: 'bsc' },
  },
};

/** All chain ids, in registry (display) order. */
const CHAIN_IDS = Object.keys(CHAINS);

/** Safe, case-insensitive lookup. Returns undefined for unknown/blank ids. */
function getChain(id) {
  if (!id || typeof id !== 'string') return undefined;
  return CHAINS[id.toLowerCase()];
}

/** All chain metas, in registry order. */
function listChains() {
  return CHAIN_IDS.map((id) => CHAINS[id]);
}

/** Ids an operator may assign as an entity `chains[]` value (the modal allowlist). */
function selectableChainIds() {
  return CHAIN_IDS.filter((id) => CHAINS[id].selectable);
}

/** Is `id` a known chain id? */
function isChainId(id) {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(CHAINS, id.toLowerCase());
}

/** Display label for a chain id, or undefined if unknown. */
function getChainLabel(id) {
  const c = getChain(id);
  return c ? c.label : undefined;
}

/**
 * K9/EO3 — per-chain LENS copy for the compiled `knowledge_pages` overview synthesis. Abstract
 * carries a `lens` block ("gaming ecosystem" / "with gaming projects at its core"); every other
 * chain gets the NEUTRAL default so a second chain lens never inherits Abstract's gaming framing.
 * @param {string} id
 * @returns {{ecosystemLabel:string, coreClause:string}}
 */
const DEFAULT_LENS = { ecosystemLabel: 'ecosystem', coreClause: '' };
function getChainLensConfig(id) {
  const c = getChain(id);
  const lens = (c && c.lens) || null;
  return {
    ecosystemLabel: (lens && lens.ecosystemLabel) || DEFAULT_LENS.ecosystemLabel,
    coreClause: (lens && lens.coreClause) || DEFAULT_LENS.coreClause,
  };
}

/**
 * SAFETY gate: are this chain's daily-recap DATA inputs verified chain-filtered?
 * Returns true ONLY for chains explicitly marked `recapDataReady: true`. Unknown or
 * unmarked chains are NOT ready (fail-closed), so EcosystemRecapService refuses to
 * generate a recap for them rather than leak another chain's data under their headline.
 */
function isRecapDataReady(id) {
  const c = getChain(id);
  return !!(c && c.recapDataReady === true);
}

/**
 * PROCESS gate: must this chain's recap be REVIEWED before it may go live?
 *
 * DEFAULT FALSE, DELIBERATELY (Lam, 2026-09-04: "auto-publish stays on until the reviewer lane
 * is proven"). This switch is the seam that lets a chain be moved onto the review-then-schedule
 * lane WITHOUT a code change once the lane has a track record — set `recapReviewRequired: true`
 * on the chain and its next generation lands `draft` + `needsReview` + `reviewRequiredHold`,
 * even with `autoPublish` on, and the only route to `published` is
 *   review-recap-draft.js --apply → ops.js schedule-headless --collection ecosystem_recaps → the drip.
 *
 * It is a SEPARATE axis from `recapDataReady`, which asks "may this chain generate a recap at
 * all?" (a data-safety assertion). This one asks "may that recap publish itself?" (a process
 * posture). A chain can be data-ready and review-required, or data-ready and auto-publishing;
 * a chain that is not data-ready never reaches either question.
 *
 * Unknown / unmarked chains are NOT review-required — the switch OPENS a gate, so its absent
 * value must be today's behaviour, not a silent lane change for every chain on the next deploy.
 */
function isRecapReviewRequired(id) {
  const c = getChain(id);
  return !!(c && c.recapReviewRequired === true);
}

/**
 * The default/home chain — the single chain the daily recap covered before it went
 * multi-chain. Mirrors `DEFAULT_RECAP_CHAIN` in backend/src/services/EcosystemRecapService.js
 * (which is where the recap's own chain-defaulting semantics live); kept here so the
 * scheduler's run ORDER doesn't have to reach into the service to know which chain is home.
 */
const DEFAULT_CHAIN_ID = 'abstract';

/**
 * Chain ids the daily-recap job may run for, in RUN ORDER.
 *
 * Fail-closed by construction: derived from `isRecapDataReady`, so a chain that has not had
 * its recap data inputs verified is simply absent from the list and never runs (the service's
 * hard-throw guard stays the second line of defence, not the first).
 *
 * ORDER: the default/home chain (Abstract) always sorts FIRST so the long-standing Abstract
 * recap runs before any newer chain — a newer chain can neither delay it nor consume the
 * cycle ahead of it. Remaining ready chains follow in registry order.
 */
function recapReadyChainIds() {
  const ready = CHAIN_IDS.filter(isRecapDataReady);
  const rest = ready.filter((id) => id !== DEFAULT_CHAIN_ID);
  return ready.includes(DEFAULT_CHAIN_ID) ? [DEFAULT_CHAIN_ID, ...rest] : rest;
}

module.exports = {
  CHAINS,
  CHAIN_IDS,
  DEFAULT_CHAIN_ID,
  getChain,
  listChains,
  selectableChainIds,
  isChainId,
  getChainLabel,
  getChainLensConfig,
  isRecapDataReady,
  isRecapReviewRequired,
  recapReadyChainIds,
};
